<template>
  <div class="alumni-login">
    <div class="inner">
      <h2 class="title text-2xl">{{ t('login') }}</h2>
      <form class="form" @submit.prevent="login">
        <FormInput
          v-model="credentials.email"
          type="email"
          name="email"
          autocomplete="username"
          :label="t('email')"
          :error="t('error.email')"
          required
        />
        <FormInput
          v-model="credentials.password"
          type="password"
          name="password"
          autocomplete="current-password"
          :label="t('password')"
          :error="t('error.password')"
          required
        />
        <div v-if="loginFailed" class="error text-md">
          <p>{{ t('error.loginFailed') }}</p>
        </div>
        <NuxtLink :to="passwordResetPageUrl" class="button-link -secondary">{{ t('resetPassword') }}</NuxtLink>
        <button class="button-secondary" type="submit">{{ t('signin') }}</button>
      </form>
    </div>
  </div>
</template>

<i18n lang="yaml">
de:
  login: Login
  email: E-Mail
  password: Passwort
  signin: Anmelden
  resetPassword: Passwort zurücksetzen
  error:
    email: Bitte geben Sie eine gültige E-Mail-Adresse ein.
    password: Bitte geben Sie Ihr Passwort ein.
    loginFailed: Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre E-Mail-Adresse und Ihr Passwort.
en:
  login: Login
  email: Email
  password: Password
  signin: Sign in
  resetPassword: Reset password
  error:
    email: Please enter a valid email address.
    password: Please enter your password.
    loginFailed: Login failed. Please check your email address and password.
</i18n>

<script setup lang="ts">
import { useAlumniSettings } from '~/app/useAlumniSettings';

const { t } = useI18n();

const { loggedIn } = useUserSession();
const { entryPageAfterLoginUrl, passwordResetPageUrl } = useAlumniSettings();

if (loggedIn.value) {
  navigateTo(entryPageAfterLoginUrl, {
    redirectCode: 302,
  });
}

const credentials = reactive({
  email: '',
  password: '',
});

watch(credentials, () => {
  loginFailed.value = false;
});

const loginFailed = ref(false);

async function login() {
  $fetch('/api/alumni/login', {
    method: 'POST',
    body: credentials,
  })
    .then(async () => {
      // Do not refresh the session, instead make a full page reload, so that the navigation reloads properly without any
      // unnecessary reactiveness magic...
      // await refreshSession();
      window.location.href = entryPageAfterLoginUrl;
    })
    .catch(() => {
      loginFailed.value = true;
    });
}
</script>

<style scoped lang="scss">
.alumni-login {
  @include grid-layout();

  & {
    padding: var(--site-inner-padding);
    width: 100%;
    max-width: var(--site-max-width);
  }

  > .inner {
    @media (--xs), (--sm) {
      padding: var(--spacing-sm) 0;
    }

    @media (--xs) {
      grid-column: 1 / -1;
    }

    @media (--sm) {
      grid-column: 2 / 24;
    }

    @media (--md), (--lg) {
      grid-column: 5 / 21;
      padding-bottom: var(--spacing-2xl);
    }

    > .form {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--spacing-xs);
      padding-top: var(--spacing-xs);

      > .form-input {
        width: 100%;
      }

      > .error {
        color: var(--color-error-red);
      }
    }
  }
}
</style>
